import { assign, isEqual, flatten, includes } from 'lodash-es'
import { before, after, toText, isEmptyArray, isNotEmptyArray } from './bits'

import envRaw from './env-raw'
import Emitter from './emitter'
import debugFields from './debug-fields'
import extractRawElm from './extract-raw-elm'

import { getElmStrShort } from './get-elm-str'

const OBSERVE_INTERVAL = 300

export default function AttrsObserver($elm, ...input) {
  const attributeFilter = flatten(input).map(toText)

  let observer = null
  let lastAttrs = getActualAttrs()
  const emitter = Emitter(`attrsObserver[elm=${getElmStrShort($elm)}]`)

  emitter.getAttrs = getAttrs
  emitter.manualTrigger = manualTrigger

  emitter.on = before(beforeOn, emitter.on)
  emitter.pre = before(beforeOn, emitter.pre)
  emitter.after = before(beforeOn, emitter.after)
  emitter.off = after(emitter.off, afterOff)
  emitter.clear = after(emitter.clear, afterOff)

  debugFields(emitter, {
    _elm: $elm,
    _attributeFilter: attributeFilter,
    _handler: handler,
    _getLastAttrs: getLastAttrs,
    _getActualAttrs: getActualAttrs
  })

  return emitter

  function getLastAttrs() {
    return lastAttrs
  }

  function getActualAttrs() {
    return AttrsMap($elm, attributeFilter)
  }

  function getAttrs() {
    return lastAttrs !== null ? lastAttrs : getActualAttrs()
  }

  function manualTrigger() {
    lastAttrs = null
    handler()
  }

  function handler() {
    const currAttrs = getActualAttrs()
    if (!isEqual(currAttrs, lastAttrs)) {
      const lastLastAttr = lastAttrs
      lastAttrs = currAttrs
      emitter.emit(lastAttrs, lastLastAttr)
    }
  }

  function beforeOn() {
    if (emitter.size() === 0) {
      observer = envRaw.useNativeAttrsObserver
        ? NativeObserver(handler, $elm, attributeFilter)
        : FallbackObserver(handler, $elm, attributeFilter)
    }
  }

  function afterOff() {
    if (observer && emitter.size() === 0) {
      observer.disconnect()
      observer = null
    }
  }
}

function NativeObserver(callback, $elm, attributeFilter) {
  const elm = extractRawElm($elm)
  const observer = new window.MutationObserver(callback)
  const config = { attributes: true }
  if (isNotEmptyArray(attributeFilter)) {
    assign(config, { attributeFilter })
  }
  observer.observe(elm, config)
  return observer
}

function FallbackObserver(callback, $elm, attributeFilter) {
  let last = AttrsMap($elm, attributeFilter)
  const id = setInterval(() => {
    const curr = AttrsMap($elm, attributeFilter)
    const cond = isEqual(last, curr)
    last = curr
    if (!cond) callback()
  }, OBSERVE_INTERVAL)
  return { disconnect: () => clearInterval(id) }
}

function AttrsMap($elm, attributeFilter) {
  const elm = extractRawElm($elm)
  const attrMap = {}
  if (!elm.attributes) {
    return attrMap
  }

  for (const attr of elm.attributes) {
    if (isEmptyArray(attributeFilter) || includes(attributeFilter, attr.name)) {
      attrMap[attr.name] = attr.value
    }
  }

  return attrMap
}
